import * as React from "react";
import { useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  ButtonBase,
  Tooltip,
  Typography,
  Avatar,
  Box,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import MenuRounded from "@mui/icons-material/MenuRounded";
import MenuOpenRounded from "@mui/icons-material/MenuOpenRounded";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import { useAuth0 } from "@auth0/auth0-react";
import LinearProgressBar from "./Forms/LinearProgressBar";
import UserMenu from "./UserMenu";
import { getPageTitle } from "../config/navigation";

export const TOPBAR_HEIGHT = 56;

export default function TopBar({ collapsed, isDesktop, onToggle, loading }) {
  const theme = useTheme();
  const { shell } = theme.palette;
  const { pathname } = useLocation();
  const { user } = useAuth0();
  const [userAnchor, setUserAnchor] = React.useState(null);

  const toggleLabel = isDesktop
    ? collapsed
      ? "Expand sidebar"
      : "Collapse sidebar"
    : "Open navigation menu";
  const initials = (user?.nickname || user?.name || "?").slice(0, 2).toUpperCase();

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        top: 0,
        bgcolor: "#fff",
        color: "text.primary",
        borderBottom: "1px solid",
        borderColor: alpha(theme.palette.common.black, 0.08),
      }}
    >
      <Toolbar
        disableGutters
        sx={{ minHeight: TOPBAR_HEIGHT, height: TOPBAR_HEIGHT, px: 2, gap: 1 }}
      >
        <IconButton
          edge="start"
          onClick={onToggle}
          aria-label={toggleLabel}
          sx={{ borderRadius: "10px" }}
        >
          {isDesktop && !collapsed ? <MenuOpenRounded /> : <MenuRounded />}
        </IconButton>
        {!isDesktop && (
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {getPageTitle(pathname)}
          </Typography>
        )}
        <Box flex={1} />
        <Tooltip title="Account & logout">
          <ButtonBase
            onClick={(e) => setUserAnchor(e.currentTarget)}
            aria-label={`Account menu${user?.nickname ? `, ${user.nickname}` : ""}`}
            aria-haspopup="true"
            aria-controls={userAnchor ? "user-menu" : undefined}
            aria-expanded={userAnchor ? "true" : undefined}
            sx={{
              gap: 1,
              py: 0.5,
              pl: 0.5,
              pr: { xs: 0.5, md: 1 },
              borderRadius: "999px",
              "&:hover": { bgcolor: alpha(theme.palette.common.black, 0.05) },
              "&:focus-visible": {
                outline: `2px solid ${shell.accent}`,
                outlineOffset: 2,
              },
            }}
          >
            <Avatar
              src={user?.picture}
              alt=""
              sx={{ width: 32, height: 32, bgcolor: shell.accent, fontSize: 14 }}
            >
              {initials}
            </Avatar>
            <Typography
              noWrap
              fontSize={14}
              fontWeight={500}
              sx={{ display: { xs: "none", md: "block" }, maxWidth: 160 }}
            >
              {user?.nickname}
            </Typography>
            <KeyboardArrowDownRounded
              fontSize="small"
              sx={{
                color: "text.secondary",
                transition: "transform 150ms",
                transform: userAnchor ? "rotate(180deg)" : "none",
              }}
            />
          </ButtonBase>
        </Tooltip>
      </Toolbar>
      {loading && (
        <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 3 }}>
          <LinearProgressBar />
        </Box>
      )}
      <UserMenu anchorEl={userAnchor} onClose={() => setUserAnchor(null)} />
    </AppBar>
  );
}
